import { type FC, useEffect } from 'react';
import { AppShell as MantineAppShell, Box, Drawer } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { Outlet } from 'react-router';
import { Footer } from '~/widgets/Footer';
import { Header } from '~/widgets/Header';
import { MobileFooterNav } from '~/widgets/MobileFooterNav';
import { Navbar } from '~/widgets/Navbar';
import { useBreakpoint } from '~/shared/lib/hooks/useBreakpoint';
import { DetectMode } from './components';
import styles from './AppShell.module.css';

export interface AppShellProps {
    withHavbar?: boolean;
}

const headerHeight = 'var(--header-height)';
const navbarWidth = 'var(--navbar-width)';
const mobileNavbarWidth = 'min(82vw, 320px)';

export const AppShell: FC<AppShellProps> = ({ withHavbar }) => {
    const [opened, { toggle, close }] = useDisclosure();
    const isMobile = useBreakpoint('mobile');
    const showDesktopNavigation = Boolean(withHavbar && !isMobile);
    const showMobileNavigation = Boolean(withHavbar && isMobile);

    useEffect(() => {
        if (!isMobile) {
            close();
        }
    }, [close, isMobile]);

    return (
        <MantineAppShell
            className={styles.root}
            header={{ height: headerHeight }}
            navbar={
                showDesktopNavigation
                    ? {
                          width: navbarWidth,
                          breakpoint: 0,
                      }
                    : undefined
            }
        >
            <Header />

            {showDesktopNavigation && (
                <MantineAppShell.Navbar className={styles.desktopNavbar}>
                    <Navbar
                        onClose={close}
                        presentation='desktop'
                    />
                </MantineAppShell.Navbar>
            )}

            {showMobileNavigation && (
                <Drawer.Root
                    classNames={{
                        body: styles.drawerBody,
                        content: styles.drawerContent,
                    }}
                    keepMounted
                    onClose={close}
                    opened={opened}
                    padding={0}
                    position='left'
                    size={mobileNavbarWidth}
                >
                    <Drawer.Overlay />

                    <Drawer.Content aria-label='Навигация'>
                        <Drawer.Body>
                            <Box
                                component='nav'
                                aria-label='Меню аккаунта'
                                h='100%'
                            >
                                <Navbar
                                    onClose={close}
                                    presentation='mobile'
                                />
                            </Box>
                        </Drawer.Body>
                    </Drawer.Content>
                </Drawer.Root>
            )}

            <MantineAppShell.Main className={styles.main}>
                <div className={styles.content}>
                    <DetectMode>
                        <Outlet />
                    </DetectMode>
                </div>

                <Footer />

                {showMobileNavigation && (
                    <div
                        className={styles.mobileFooterSafeArea}
                        data-testid='mobile-footer-safe-area'
                        aria-hidden='true'
                    />
                )}
            </MantineAppShell.Main>

            {showMobileNavigation && (
                <MobileFooterNav
                    opened={opened}
                    onNavigate={close}
                    onMoreClick={toggle}
                />
            )}
        </MantineAppShell>
    );
};
